import React, { Component } from 'react'
import { observer } from 'mobx-react'
import i18n from 'i18next'
import backend from 'i18next-xhr-backend'
import { reactI18nextModule } from 'react-i18next'
import * as R from 'ramda'
import { ErrorBoundary } from '@sentry/react'
import {
    BrowserRouter as Router,
    Route,
    Switch,
} from 'react-router-dom'
import NotificationSystem from 'react-notification-system'
import MixpanelManager from './utils/MixpanelManager'

import QuestionCategory from './QuestionCategory'
import UserSearch from './User/UserSearch'
import ProductSearch from './Product/ProductSearch'
import QuestionMenu from './QuestionMenu'
import './assets/vendor/bootstrap-3.3.7/css/bootstrap.css'
import AppLoading from './components/AppLoading'
import Survey from './components/Survey'
import MaxbiSearch from './components/Survey/MaxbiSearch'
import PortfolioSearch from './components/Portfolio/PortfolioSearch'
import VenueSearch from './components/Venue/VenueSearch'
import DivisionSearch from './components/Division/DivisionSearch'
import Home from './components/Home'
import HomeV3 from './components/HomeV3'
import ClientSearch from './components/Client/ClientSearch'
import Header from './components/Header/index'
import NewHeader from './components/Header/NewHeader'
import SurveySearch from './components/SurveySearch'
import Login from './Login'
import ProtectedRoute from './ProtectedRoute'
import ChangePassword from './components/ChangePassword'
import MergeFields from './components/MergeFields'
import MergeFieldCreateEdit from './components/MergeFields/MergeFieldCreateEdit'
import DynamicDashboardV3 from './components/DynamicDashV3'
import DataExplorer from './components/DataExplorer'
import SalesIntelligence from './components/SalesIntelligence'

import './assets/css/test.css'
import './assets/scss/global.scss'
import LoginChoose from './Login/LoginChoose'
import MandatoryQuestions from './QList'
import PasswordReset from './Login/PasswordReset'
import PasswordResetForm from './Login/PasswordResetForm'
import Sidebar from './components/Sidebar'

import DistributeStore from './components/Distribute/DistributeStore'
import Intranet from './components/Intranet'
import GenericError from './components/GenericError'
import TemplateSearch from './components/TemplateSearch'
import EmailTemplateSearch from './components/TemplateSearch/EmailTemplateSearch'
import PresentationTemplateSearch from './components/TemplateSearch/PresentationTemplateSearch'
import PresentationTemplate from './components/Presentations/PresentationTemplate'
import Presentations from './components/Presentations'
import Presentation from './components/Presentations/Presentation'

import './assets/css/ssm.scss'

const PresentationRoute = ({
    store, match, history, location,
}) => (
    <Presentation store={store} history={history} match={match} location={location} />
)

const PresentationsRoute = ({
    store, match, history, location,
}) => (
    <Presentations store={store} history={history} match={match} location={location} />
)

const PresentationTemplateRoute = ({
    store, match, history, location,
}) => (
    <PresentationTemplate store={store} history={history} match={match} location={location} />
)

@observer
export default class App extends Component {
    async componentDidMount() {
        const { store } = this.props

        store._notificationSystem = this.refs.notificationSystem

        const loadPath = `${process.env.ASSETS_URL}/assets/i18n/{{lng}}.json`

        await i18n
            .use(reactI18nextModule) // passes i18n down to react-i18next
            .use(backend)
            .init({
                backend: {
                    loadPath,
                },
                initImmediate: false,
                lng: store.langcode,
                fallbackLng: 'en_GB',
                keySeparator: false, // we do not use keys in form messages.welcome
                interpolation: {
                    escapeValue: false, // react already safes from xss
                },
            }, async (e, t) => {
                console.log(`i18next ready ${t('breadcrumb_home')}`)
                store.mixpanel = new MixpanelManager()
                store.i18nLoaded = true
                await store.init()
            })

        // if (!(process?.env?.MIXPANEL_DISABLED ?? false)) {
        store.mixpanel.init(process.env.MIXPANEL_SECRET ?? '', {
            debug: true,
            track_pageview: false,
            persistence: 'localStorage',
            record_sessions_percent: 0,
            record_heatmap_data: false,
            api_host: 'https://api-eu.mixpanel.com',
            autocapture: false,
        })
        // }
    }

    getLang = () => {
        const { store } = this.props
        store.setLang('en_GB')
    }

    render() {
        const { store } = this.props
        const {
            serviceStatus, isMessageCollapsed, saveCollapse, shouldShow,
        } = store
        const ssm = {
            id: R.pathOr(undefined, ['id'], serviceStatus),
            status: R.pathOr(undefined, ['status'], serviceStatus),
            collapsible: R.pathOr(undefined, ['collapsible'], serviceStatus),
            textColor: R.pathOr('#2B3C44', ['textColor'], serviceStatus),
            bgColor: R.pathOr('#F8EEE5', ['bgColor'], serviceStatus),
        }

        if (!store.i18nLoaded) return <AppLoading text="Loading files" />

        const showSidebar = store.loggedIn
            && store.tokenLoaded
            && !store.isLoginChoose

        return (
            <ErrorBoundary fallback={<GenericError />}>
                <Router>
                    <div className="app-wrapper">
                        {showSidebar
                            && (
                                <div className="left-nav-wrapper">
                                    <Sidebar store={store} />
                                </div>
                            )}
                        <div className="main-layout">
                            <div>
                                {store.loggedIn
                                    && store.tokenLoaded
                                    && (
                                        process.env.USE_NEW_HEADER_UI ? <NewHeader store={store} /> : <Header store={store} />
                                    )}

                                {ssm.id
                                    && (!ssm.collapsible || (!isMessageCollapsed(ssm.id) && shouldShow)) && (
                                    <div id="ssm-header" style={{ backgroundColor: ssm.bgColor, color: ssm.textColor }}>
                                        <div className="ssm" dangerouslySetInnerHTML={{ __html: ssm.status }} />
                                        {
                                            ssm.collapsible ? (
                                                <button
                                                    type="button"
                                                    onClick={() => saveCollapse(ssm.id)}
                                                >
                                                    <i className="far fa-x" />
                                                </button>
                                            ) : null
                                        }
                                    </div>
                                )}

                                <NotificationSystem ref={(el) => { store._notificationSystem = el }} />
                                <div className={`content ${store.loggedIn ? 'layout' : ''}`}>
                                    { store.loggedIn && store.failedToLoadSurvey
                                        && (
                                            <div style={{ 'font-size': '1.4em', 'text-align': 'center', 'margin-top': '100px' }}>
                                                {i18n.t('msg_survey_load_failed')}
                                                {store.failedQuestions && store.failedQuestions.length > 0
                                                && (
                                                    <div>
                                                        {i18n.t('msg_questions_load_failed')}
                                                        :
                                                        <br />
                                                        {store.failedQuestions.map((fq) => <div>{fq}</div>)}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    { store.showLoading()
                                        && <AppLoading text="Loading components" />}
                                    { !store.loggedIn && !store.loginRedirect && store.hasValidSession
                                        && (
                                            <div
                                                style={{
                                                    width: '300px',
                                                    position: 'absolute',
                                                    top: '50%',
                                                    left: '50%',
                                                    transform: 'translate(-50%, -50.5%)',
                                                    border: '1px solid #CCC',
                                                    borderRadius: '5px',
                                                    backgroundColor: '#FFF',
                                                    padding: '10px',
                                                }}
                                                dangerouslySetInnerHTML={{ __html: i18n.t('loggedOut', { 1: `?pagePath=${window.location.pathname}` }) }}
                                            />
                                        )}
                                    <Switch>
                                        <ProtectedRoute
                                            store={store}
                                            path="/login/choose"
                                            title="login"
                                            isLoginChoose
                                            component={LoginChoose}
                                            customRule={() => store.allowLoginChoose()}
                                            roleRedirect="/home/e/01011"
                                        />
                                        <Route
                                            path="/login*"
                                            render={({ match, history, location }) => (
                                                <Login store={store} history={history} match={match} location={location} />
                                            )}
                                        />
                                        <Route
                                            path="/passwordreset/resetform/UUID/:uuid"
                                            render={({ match, history, location }) => (
                                                <PasswordResetForm store={store} history={history} match={match} location={location} />
                                            )}
                                        />
                                        <Route
                                            path="/passwordreset"
                                            render={({ match, history, location }) => (
                                                <PasswordReset store={store} history={history} match={match} location={location} />
                                            )}
                                        />

                                        <ProtectedRoute
                                            store={store}
                                            path="/presentations/:id"
                                            title="presentation"
                                            component={PresentationRoute}
                                            customRule={store.presentationAccess}
                                            roleRedirect="/home/e/01011"
                                        />
                                        <ProtectedRoute
                                            store={store}
                                            exact
                                            path="/presentations"
                                            title="presentations"
                                            component={PresentationsRoute}
                                            customRule={store.presentationAccess}
                                            roleRedirect="/home/e/01011"
                                        />
                                        <ProtectedRoute
                                            store={store}
                                            path="/portfolio"
                                            title="portfolio"
                                            component={PortfolioSearch}
                                            role={['100', '110', '120', '130']}
                                            roleRedirect="/home/e/01011"
                                        />
                                        <ProtectedRoute
                                            store={store}
                                            path="/venue"
                                            title="venue"
                                            component={VenueSearch}
                                            customRule={store.isExploriSuper}
                                            roleRedirect="/home/e/01011"
                                        />
                                        <ProtectedRoute
                                            roleRedirect="/home/e/01011"
                                            role={['100', '110', '120']}
                                            component={DivisionSearch}
                                            store={store}
                                            path="/division"
                                            title="division"
                                        />
                                        <ProtectedRoute
                                            store={store}
                                            path="/home"
                                            title="home"
                                            component={process.env.USE_NEW_HOME ? Home : HomeV3}
                                        />
                                        <ProtectedRoute
                                            store={store}
                                            roleRedirect="/home/e/01011"
                                            customRule={store.isExploriAdmin}
                                            path="/intranet/mergefields/:mergefieldId"
                                            title="MergeField"
                                            component={MergeFieldCreateEdit}
                                        />
                                        <ProtectedRoute
                                            store={store}
                                            roleRedirect="/home/e/01011"
                                            customRule={store.isExploriAdmin}
                                            path="/intranet/mergefields"
                                            title="MergeField"
                                            component={MergeFields}
                                        />
                                        <ProtectedRoute store={store} path="/user/updatepassword" title="update password" component={ChangePassword} />
                                        <ProtectedRoute
                                            store={store}
                                            path="/survey/:id/:tab?"
                                            component={({ match, history }) => (
                                                <Survey
                                                    history={history}
                                                    store={store}
                                                    match={match}
                                                    distributeStore={new DistributeStore(store, match.params.id, history)}
                                                />
                                            )}
                                        />
                                        <ProtectedRoute
                                            roleRedirect="/home/e/01011"
                                            role={['100', '110', '120', '130', '140', '160']}
                                            surveySearchType="survey"
                                            component={SurveySearch}
                                            store={store}
                                            path="/survey"
                                            title="survey"
                                        />
                                        <ProtectedRoute
                                            roleRedirect="/home/e/01011"
                                            customRule={store.canAccessMaxbiDashboard}
                                            component={DynamicDashboardV3}
                                            store={store}
                                            path="/maxbi/v4dashboard"
                                            title="Dashboard"
                                        />
                                        <ProtectedRoute
                                            roleRedirect="/home/e/01011"
                                            role={store.getMaxbiStatus().maxbi ? ['100', '110', '120', '130', '140', '160'] : []}
                                            report
                                            component={MaxbiSearch}
                                            store={store}
                                            path="/maxbi"
                                            title="maxbi"
                                        />
                                        <ProtectedRoute surveySearchType="report" component={SurveySearch} store={store} path="/report" title="report" />
                                        <ProtectedRoute
                                            component={DynamicDashboardV3}
                                            store={store}
                                            path="/company/dashboard"
                                            title="dashboard"
                                        />
                                        <ProtectedRoute
                                            roleRedirect="/home/e/01011"
                                            component={SalesIntelligence}
                                            role={store.userData?.flags?.isSalesIntelligenceEnabled ? ['100', '110', '120', '130', '140', '150', '170'] : []}
                                            store={store}
                                            path="/salesIntelligence"
                                            title="dashboard"
                                        />
                                        <ProtectedRoute
                                            roleRedirect="/home/e/01011"
                                            role={['100', '110', '120', '130']}
                                            component={MandatoryQuestions}
                                            store={store}
                                            path="/mandatoryquestions/"
                                            title="Mandatory Questions"
                                        />
                                        <ProtectedRoute
                                            roleRedirect="/intranet"
                                            customRule={async () => store.isExploriAdmin()}
                                            component={Intranet}
                                            store={store}
                                            path="/intranet"
                                            title="Intranet"
                                        />
                                        <ProtectedRoute
                                            roleRedirect="/home/e/01011"
                                            role={['100', '110']}
                                            component={QuestionMenu}
                                            store={store}
                                            path="/question"
                                            title="question"
                                        />
                                        <ProtectedRoute
                                            roleRedirect="/home/e/01011"
                                            component={EmailTemplateSearch}
                                            role={['100', '110', '120', '130', '140', '160']}
                                            store={store}
                                            path="/email-templates"
                                            title="Email Templates"
                                        />
                                        <ProtectedRoute
                                            roleRedirect="/home/e/01011"
                                            component={TemplateSearch}
                                            role={['100', '110', '120', '130', '140', '160']}
                                            store={store}
                                            path="/survey-templates"
                                            title="Survey Templates"
                                        />
                                        <ProtectedRoute
                                            roleRedirect="/home/e/01011"
                                            role={['100', '110', '120', '130', '140', '160']}
                                            store={store}
                                            path="/presentation-templates/:id"
                                            title="Presentation Template"
                                            component={PresentationTemplateRoute}
                                        />
                                        <ProtectedRoute
                                            roleRedirect="/home/e/01011"
                                            component={PresentationTemplateSearch}
                                            role={['100', '110', '120', '130', '140', '160']}
                                            store={store}
                                            exact
                                            path="/presentation-templates"
                                            title="Presentation Templates"
                                        />
                                        <ProtectedRoute
                                            roleRedirect="/home/e/01011"
                                            role={['100']}
                                            customRule={store.isExploriSuper}
                                            component={QuestionCategory}
                                            store={store}
                                            path="/questioncategory"
                                            title="question category"
                                        />
                                        <ProtectedRoute component={UserSearch} store={store} path="/user" title="user" />
                                        <ProtectedRoute component={ProductSearch} store={store} path="/event" title="event" />
                                        <ProtectedRoute
                                            roleRedirect="/home/e/01011"
                                            role={['100']}
                                            customRule={store.isExploriSuper}
                                            component={ClientSearch}
                                            store={store}
                                            path="/client"
                                            title="client"
                                        />
                                        <ProtectedRoute
                                            component={DataExplorer}
                                            store={store}
                                            path="/dataexplorer"
                                            title="Data Explorer"
                                        />
                                        <ProtectedRoute component={process.env.USE_NEW_HOME ? Home : HomeV3} store={store} isExact path="/" title="home" />

                                        <Route
                                            store={store}
                                            render={() => (
                                                <div>not found app</div>
                                            )}
                                        />
                                    </Switch>
                                </div>
                            </div>
                        </div>
                    </div>
                </Router>
            </ErrorBoundary>
        )
    }
}
